import {useEffect, useState, useCallback, useRef} from 'react'
import {Modal} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {Link} from 'react-router-dom'
import {ROUTES} from '../../../_gori/constants'
import {sanitizeHTML} from '../blog/utils/sanitize'
import {formatDate} from '../blog/utils/dateFormatter'

type Release = {
  id: number
  title: string
  slug: string
  content: string
  excerpt?: string
  featured_image: string | null
  date: string
  acf?: {
    shipbae_release_video_url?: string
    additional_image?: string
  }
}

const API_URL = process.env.REACT_APP_API_URL || ''

type ReleaseModalProps = {
  slug: string | null
  onClose: () => void
}

export function ReleaseModal({slug, onClose}: ReleaseModalProps) {
  const intl = useIntl()
  const [release, setRelease] = useState<Release | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  const fetchRelease = useCallback(async () => {
    if (!slug) return

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()
    setLoading(true)
    setError(null)

    try {
      const res = await fetch(`${API_URL}/api/releases/release?slug=${slug}`, {
        signal: abortControllerRef.current.signal,
      })

      if (!res.ok) {
        throw new Error(res.status === 404 ? 'Release not found' : 'Failed to load release')
      }

      const data: Release = await res.json()
      setRelease(data)
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return

      console.error('Error fetching release:', err)
      setError(err instanceof Error ? err.message : 'Failed to load release')
    } finally {
      setLoading(false)
    }
  }, [slug])

  useEffect(() => {
    if (slug) {
      fetchRelease()
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [fetchRelease, slug])

  return (
    <Modal show={!!slug} onHide={onClose} size='xl' centered>
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage({id: 'DETAIL'})}</Modal.Title>
      </Modal.Header>
      <Modal.Body className='pb-0'>
        {loading && (
          <div className='text-center py-10'>
            <div className='spinner-border text-primary' role='status'>
              <span className='visually-hidden'>Loading...</span>
            </div>
          </div>
        )}

        {error && (
          <div className='alert alert-danger text-center' role='alert'>
            {error}
          </div>
        )}

        {!loading && !error && release && (
          <article>
            <div className='release-modal-header mb-5'>
              {release.acf?.shipbae_release_video_url ? (
                <video controls className='rounded mb-4 release-modal-media'>
                  <source src={release.acf.shipbae_release_video_url} type='video/mp4' />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <>
                  {release.featured_image && (
                    <img
                      src={release.featured_image}
                      srcSet={`${release.featured_image}?w=400 400w, ${release.featured_image}?w=800 800w`}
                      sizes='(max-width: 768px) 100vw, 800px'
                      alt={release.title}
                      className='rounded mb-4 release-modal-media'
                      loading='lazy'
                    />
                  )}
                  {release.acf?.additional_image && (
                    <img
                      src={release.acf.additional_image}
                      srcSet={`${release.acf.additional_image}?w=400 400w, ${release.acf.additional_image}?w=800 800w`}
                      sizes='(max-width: 768px) 100vw, 800px'
                      alt={`${release.title} - additional`}
                      className='rounded mb-4 release-modal-media'
                      loading='lazy'
                    />
                  )}
                </>
              )}

              <h3 className='mb-3 release-modal-title'>{sanitizeHTML(release.title, false)}</h3>
              {release.date && (
                <p className='text-muted small mb-4 release-modal-date'>
                  {formatDate(release.date)}
                </p>
              )}
            </div>

            <div
              className='release-modal-content'
              dangerouslySetInnerHTML={{__html: sanitizeHTML(release.content)}}
            />
          </article>
        )}
      </Modal.Body>
      <Modal.Footer className='justify-content-start border-0 pt-0 pb-7'>
        <Link
          to={ROUTES.AUTH.BASE}
          className='btn btn-outline btn-primary-deep darker hover-orange fs-4 fw-boldest text-capitalize rounded-pill py-3 px-6'
          onClick={onClose}
        >
          {intl.formatMessage({id: 'EXPLORE_SHIPBAE'})}
        </Link>
      </Modal.Footer>
    </Modal>
  )
}
